import { Action, ActionCreator, ActionReducer, ActionType, createReducer, ReducerTypes } from '@ngrx/store';
import { OnReducer } from '@ngrx/store/src/reducer_creator';

export interface RehydrateRecucerConfig {
  key: string;
  storage?: 'sessionStorage' | 'localStorage';
}

/**
 * Persist (rehydrate) the state in the session storage
 *
 * Example of use:
 import { createRehydrateReducer } from '@core/utils/rehydrate-reducer';
 export const exampleReducer = createRehydrateReducer(
  { key: 'key1' },
  INITIAL_STATE,
  on(setAction, (state, { flag }) => ({ ...state, flag })),
 );
 *
 * @param config key: key of the state to be rehydrated, storage: 'sessionStorage' | 'localStorage'
 * @param initialState initial state of the state to be rehydrated
 * @param ons function to be executed when the action is dispatched
 * @returns reducer function
 */
export function createRehydrateReducer<S, A extends Action = Action>(
  config: RehydrateRecucerConfig,
  initialState: S,
  ...ons: ReducerTypes<S, ActionCreator[]>[]
): ActionReducer<S, A> {
  const { key, storage } = config;
  const item = storage === 'sessionStorage' ? sessionStorage.getItem(key) : localStorage.getItem(key);
  const newInitialState = (item && JSON.parse(item)) ?? initialState;
  const newOns: ReducerTypes<S, ActionCreator[]>[] = [];

  ons.forEach((oldOn: ReducerTypes<S, ActionCreator[]>) => {
    const newReducer: OnReducer<S, ActionCreator[]> = (state: S, action: ActionType<ActionCreator[][number]>) => {
      const newState = oldOn.reducer(state, action);

      if (storage === 'sessionStorage') {
        sessionStorage.setItem(key, JSON.stringify(newState));
      } else {
        localStorage.setItem(key, JSON.stringify(newState));
      }

      return newState;
    };
    newOns.push({ ...oldOn, reducer: newReducer });
  });

  return createReducer(newInitialState, ...newOns);
}
